// SharedSpaceOverview — aggregated stats hero + calendar for a shared
// space. Pulls /api/orders/by-month?space=true plus /api/analytics/spend
// and shows member contribution chips. Mirrors MyDashboardOverview's
// structure but aggregates across every member of the caller's space.
//
// Composes with SpaceCard / BillSplitPanel / BudgetProgressPanel inside
// the parent SharedSpaceView.

import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, ShoppingBag, Wallet, Users } from "lucide-react";
import { api } from "@/lib/api";

const PLATFORM_COLORS = {
  blinkit: "#F8CB46",
  zepto: "#8B5CF6",
  swiggy_instamart: "#FC8019",
  swiggy: "#FC8019",
  instamart: "#FC8019",
  zomato: "#E23744",
  bigbasket: "#84C225",
  dunzo: "#00D4AA",
  unknown: "#94A3B8",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n) || 0));

const platformColor = (p) =>
  PLATFORM_COLORS[(p || "").toLowerCase()] || PLATFORM_COLORS.unknown;

function buildCalendarMatrix(year, month) {
  const first = new Date(year, month - 1, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export default function SharedSpaceOverview({ spaceState }) {
  const today = useMemo(() => new Date(), []);
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const [ordersData, setOrdersData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api
        .get("/orders/by-month", { params: { year, month, space: true } })
        .then((r) => r.data)
        .catch(() => null),
      api
        .get("/analytics/spend", { params: { months: 6 } })
        .then((r) => r.data)
        .catch(() => null),
    ]).then(([o, a]) => {
      if (!alive) return;
      setOrdersData(o);
      setAnalyticsData(a);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
    // Refetch when the user switches into a different space (rare but
    // possible during a join → leave → join sequence) — keeps the
    // calendar honest without a Dashboard reload.
  }, [year, month, spaceState?.spaceId]);

  const matrix = useMemo(() => buildCalendarMatrix(year, month), [year, month]);
  const daysMap = ordersData?.days || {};
  const totalOrders = ordersData?.totalOrders || 0;
  const totalSpend = ordersData?.totalSpend || 0;
  const memberCount = spaceState?.memberCount || 1;

  // Build per-member contribution breakdown for the chip row.
  const memberById = useMemo(() => {
    const m = {};
    (spaceState?.members || []).forEach((mb) => {
      m[mb.user_id] = mb;
    });
    return m;
  }, [spaceState]);

  const memberSpend = useMemo(() => {
    const out = {};
    Object.values(daysMap).forEach((dayOrders) => {
      dayOrders.forEach((o) => {
        const uid = o.user_id || "unknown";
        out[uid] = (out[uid] || 0) + (o.amount || 0);
      });
    });
    return out;
  }, [daysMap]);

  const memberChips = useMemo(() => {
    const ids = Object.keys(memberSpend).sort(
      (a, b) => memberSpend[b] - memberSpend[a],
    );
    return ids.map((uid) => ({
      user_id: uid,
      name: memberById[uid]?.name || "Member",
      amount: memberSpend[uid] || 0,
    }));
  }, [memberSpend, memberById]);

  // ── render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cartiq-card flex items-center gap-3 text-sm text-[#64748B]" data-testid="shared-space-overview-loading">
        <div className="w-2 h-2 rounded-full bg-[#3EB489] animate-pulse" />
        Loading shared-space stats…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="shared-space-overview">
      {/* ── Stats hero (aggregated across the space) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <div className="cartiq-card !p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            <ShoppingBag className="w-3.5 h-3.5 text-[#3EB489]" />
            Total Orders
          </div>
          <div
            className="mt-2 text-4xl font-extrabold font-mono-num text-[#0F172A] tracking-tight"
            data-testid="shared-space-total-orders"
          >
            {totalOrders}
          </div>
          <div className="mt-1 text-xs text-[#64748B]">
            {MONTH_NAMES[month - 1]} {year}
          </div>
        </div>
        <div className="cartiq-card !p-5">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            <Wallet className="w-3.5 h-3.5 text-[#2D8C6E]" />
            Total Spend
          </div>
          <div
            className="mt-2 text-4xl font-extrabold font-mono-num text-[#2D8C6E] tracking-tight"
            data-testid="shared-space-total-spend"
          >
            {formatINR(totalSpend)}
          </div>
          <div className="mt-1 text-xs text-[#64748B]">
            Across {memberCount} member{memberCount === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* ── Member contribution chips ── */}
      {memberChips.length > 0 ? (
        <div className="cartiq-card !py-3 !px-4">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
            <Users className="w-3.5 h-3.5 text-[#3EB489]" />
            Contribution This Month
          </div>
          <div
            className="mt-2 flex flex-wrap gap-2"
            data-testid="shared-space-member-chips"
          >
            {memberChips.map((c) => (
              <div
                key={c.user_id}
                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-[#F1F5F9] border border-[#E2E8F0]"
                data-testid={`member-chip-${c.user_id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#3EB489]" />
                <span className="text-xs font-bold text-[#334155]">
                  {c.name}
                </span>
                <span className="text-xs font-extrabold font-mono-num text-[#0F172A]">
                  {formatINR(c.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ── Calendar ── */}
      <div className="cartiq-card">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#3EB489]" />
          <h3 className="font-display uppercase text-base text-[#0F172A]">
            Month View — {MONTH_NAMES[month - 1]} {year}
          </h3>
        </div>

        <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-[#94A3B8]">
          {DAY_NAMES.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1.5">
          {matrix.flat().map((day, idx) => {
            if (day === null) {
              return <div key={idx} className="aspect-square" />;
            }
            const orders = daysMap[String(day)] || [];
            const hasOrders = orders.length > 0;
            const isSelected = selectedDay === day;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedDay(hasOrders ? day : null)}
                disabled={!hasOrders}
                data-testid={`shared-space-day-${day}`}
                className={`aspect-square rounded-md border text-xs font-bold transition-colors relative ${
                  isSelected
                    ? "border-[#3EB489] bg-[#3EB489]/10 text-[#2D8C6E]"
                    : hasOrders
                      ? "border-[#E2E8F0] bg-white text-[#334155] hover:border-[#3EB489]/40"
                      : "border-[#F1F5F9] bg-[#F8FAFC] text-[#CBD5E1] cursor-default"
                }`}
              >
                <span className="absolute top-1 left-1.5">{day}</span>
                {hasOrders ? (
                  <div className="absolute bottom-1 right-1 flex items-center gap-[2px]">
                    {orders.slice(0, 3).map((o, i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: platformColor(o.platform) }}
                      />
                    ))}
                    {orders.length > 3 ? (
                      <span className="text-[8px] text-[#64748B] ml-0.5">
                        +{orders.length - 3}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Day detail — list of orders on the selected day */}
        {selectedDay && daysMap[String(selectedDay)] ? (
          <div
            className="mt-4 pt-4 border-t border-[#E2E8F0]"
            data-testid="shared-space-day-detail"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
              {MONTH_NAMES[month - 1]} {selectedDay}, {year}
            </div>
            <ul className="mt-2 space-y-2">
              {daysMap[String(selectedDay)].map((o, i) => {
                const member = memberById[o.user_id];
                return (
                  <li
                    key={i}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]"
                    data-testid={`shared-space-day-order-${i}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: platformColor(o.platform) }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#334155] capitalize truncate">
                        {(o.platform || "Other").replace("_", " ")}
                        {member ? (
                          <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#2D8C6E]">
                            {member.name}
                          </span>
                        ) : null}
                      </div>
                      {o.itemCount ? (
                        <div className="text-[11px] text-[#64748B]">
                          {o.itemCount} item{o.itemCount === 1 ? "" : "s"}
                        </div>
                      ) : (
                        <div className="text-[11px] text-[#64748B] italic">
                          Auto-tracked
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-extrabold font-mono-num text-[#0F172A]">
                      {formatINR(o.amount)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Expose helpers for SharedSpaceView (analytics-derived budget tier)
SharedSpaceOverview.useAnalyticsForCurrentMonth = function useAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get("/analytics/spend", { params: { months: 1 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  return data;
};
